import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, SearchIcon, BanknoteIcon, CheckCircleIcon, FileTextIcon, MessageSquareIcon, AlertOctagonIcon } from 'lucide-react';
const ProtocolStep = ({
  number,
  title,
  desc,
  icon: Icon
}: {
  number: string;
  title: string;
  desc: string;
  icon: any;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl relative overflow-hidden group hover:border-amber-500/50 transition-colors" data-id="element-8">
    <div className="absolute top-0 right-0 p-4 opacity-10 font-black text-6xl text-zinc-500 select-none" data-id="element-9">
      {number}
    </div>
    <div className="mb-4 p-3 bg-amber-500/10 w-fit rounded-lg text-amber-500" data-id="element-10">
      <Icon className="w-6 h-6" data-id="element-11" />
    </div>
    <h4 className="text-xl font-bold text-white mb-2" data-id="element-12">{title}</h4>
    <p className="text-zinc-400" data-id="element-13">{desc}</p>
  </motion.div>;
const ChecklistItem = ({
  text
}: {
  text: string;
}) => <div className="flex items-start gap-3" data-id="element-14">
    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 shrink-0" data-id="element-15" />
    <span className="text-zinc-300" data-id="element-16">{text}</span>
  </div>;
export function BenefitsSection() {
  return <section className="py-14 px-4 bg-[#0a0a0a]" data-id="element-17">
      <div className="max-w-6xl mx-auto" data-id="element-18">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-19">
          <motion.span initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} className="text-amber-500 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-20">
            La Solución
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-21">
            Kit «Alquilá Sin Que Te Estafen»
          </motion.h2>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-22">
            Un PDF práctico, directo, sin jerga legal. <br data-id="element-23" />
            <span className="text-white" data-id="element-24">
              Para abrir con el celular cuando te están apurando.
            </span>
          </p>
        </div>

        {/* Protocolo 3C */}
        <div className="grid md:grid-cols-3 gap-5 mb-16" data-id="element-25">
          <ProtocolStep number="01" icon={ShieldCheckIcon} title="Confirmar" desc="Verificar identidad y propiedad con métodos que no pueden falsificar." data-id="element-26" />
          <ProtocolStep number="02" icon={SearchIcon} title="Cruzar" desc="Cruzar datos con fuentes reales y bases de datos públicas." data-id="element-27" />
          <ProtocolStep number="03" icon={BanknoteIcon} title="Cobrar (Pagar)" desc="Pagar solo cuando tenés la evidencia completa y verificada." data-id="element-28" />
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-29">
          {/* Left: What's inside */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="space-y-8" data-id="element-30">
            <h3 className="text-3xl font-bold text-white" data-id="element-31">
              Carpeta Lista: <br data-id="element-32" />
              <span className="text-zinc-500" data-id="element-33">
                Todo organizado para no improvisar.
              </span>
            </h3>

            <div className="space-y-4" data-id="element-34">
              <ChecklistItem text="Checklist «Antes de visitar / reservar / transferir»" data-id="element-35" />
              <ChecklistItem text="Señales rojas más comunes (las que ignorás por apuro)" data-id="element-36" />
              <ChecklistItem text="Guiones de WhatsApp listos para copiar/pegar" data-id="element-37" />
              <ChecklistItem text="Orden seguro de pagos y constancias" data-id="element-38" />
              <ChecklistItem text="Casos típicos reales para entrenar el ojo" data-id="element-39" />
            </div>
          </motion.div>

          {/* Right: Who is this for */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8" data-id="element-40">
            <div className="mb-8" data-id="element-41">
              <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2" data-id="element-42">
                <CheckCircleIcon className="text-green-500 w-5 h-5" data-id="element-43" />
                Para vos si:
              </h4>
              <ul className="space-y-2 text-zinc-400 text-sm" data-id="element-44">
                <li data-id="element-45">• Tenés 30+ y querés alquilar sin dramas</li>
                <li data-id="element-46">• Usás portales y redes y querés filtrar rápido</li>
                <li data-id="element-47">• Te sirve una solución clara y accionable</li>
              </ul>
            </div>

            <div data-id="element-48">
              <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2" data-id="element-49">
                <AlertOctagonIcon className="text-red-500 w-5 h-5" data-id="element-50" />
                No es para vos si:
              </h4>
              <ul className="space-y-2 text-zinc-400 text-sm" data-id="element-51">
                <li data-id="element-52">• Buscás «trucos» para saltearte requisitos</li>
                <li data-id="element-53">
                  • Querés que otro lo haga por vos (esto es un kit de
                  ejecución)
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        <div className="mt-12 text-center" data-id="element-54">
          <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-55">
            Vas a dejar de tomar decisiones críticas <br data-id="element-56" />
            <span className="text-amber-500" data-id="element-57">
              con ansiedad y sin evidencia.
            </span>
          </p>
        </div>
      </div>
    </section>;
}